import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../lib/supabase';

const PUSH_ENABLED = true;

/**
 * Initialize push notifications on native iOS.
 * Call after the user is authenticated and data is loaded.
 * Safe to call multiple times — re-registers on every launch to handle token rotation.
 */
export async function initPushNotifications(userId: string): Promise<void> {
  if (!PUSH_ENABLED) return;
  if (!Capacitor.isNativePlatform()) return;

  try {
    // Request permission
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      console.info('[push] Permission not granted');
      return;
    }

    // Register with APNs
    await PushNotifications.register();

    // Listen for registration success — save token to Supabase
    PushNotifications.addListener('registration', async (token) => {
      console.info('[push] Registered, token:', token.value.slice(0, 12) + '...');
      try {
        await supabase
          .from('profiles')
          .update({ apns_token: token.value } as never)
          .eq('id' as never, userId);
      } catch (err) {
        console.warn('[push] Failed to save APNs token:', err);
      }
    });

    // Registration error
    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] Registration failed:', err);
    });

    // Foreground notification — dispatch event for in-app handling
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.info('[push] Foreground notification:', notification.title);
      window.dispatchEvent(
        new CustomEvent('push-notification-received', {
          detail: {
            title: notification.title,
            body: notification.body,
            data: notification.data,
          },
        }),
      );
    });

    // Notification tap — navigate based on data.type
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data;
      const type = data?.type as string | undefined;
      switch (type) {
        case 'overlap':
          window.dispatchEvent(new CustomEvent('navigate-to-overlaps'));
          break;
        case 'message':
          window.dispatchEvent(
            new CustomEvent('navigate-to-messages', { detail: { partnerId: data?.partnerId } }),
          );
          break;
        case 'group_message':
          window.dispatchEvent(
            new CustomEvent('navigate-to-messages', { detail: { groupId: data?.groupId } }),
          );
          break;
        case 'meetup_proposal':
          window.dispatchEvent(
            new CustomEvent('navigate-to-messages', { detail: { groupId: data?.groupId } }),
          );
          break;
        case 'friend_request':
        case 'friend_accepted':
        case 'contact_joined':
          window.dispatchEvent(new CustomEvent('navigate-to-friends'));
          break;
        case 'roster_overlap':
          window.dispatchEvent(new CustomEvent('navigate-to-overlaps'));
          break;
        case 'nudge':
          window.dispatchEvent(new CustomEvent('open-roster-import'));
          break;
        default:
          // No specific navigation
          break;
      }
    });
  } catch (err) {
    console.warn('[push] Init failed:', err);
  }
}

/**
 * Clear the APNs token from the user's profile.
 * Call on sign-out to stop receiving push notifications.
 */
export async function removePushToken(userId: string): Promise<void> {
  if (!PUSH_ENABLED) return;
  try {
    await supabase
      .from('profiles')
      .update({ apns_token: null } as never)
      .eq('id' as never, userId);
  } catch (err) {
    console.warn('[push] Failed to clear APNs token:', err);
  }
}
